package org.matthewo.api

import kotlin.jvm.JvmName
import kotlinx.serialization.Serializable
import kotlin.math.roundToInt
import org.matthewo.symbolprocessor.Period.*
import org.matthewo.schema.MetricsMeta
import org.matthewo.schema.games.*
import org.matthewo.math.plus
import org.matthewo.math.convert

@Serializable
data class MetricsDecodeAPI<N : Number>(
    override val total: N,
    override val npTotal: N,
    override val auto: N,
    override val teleop: N,
    override val endgame: N,
    override val penalty: N,
    override val apClassified: N,
    override val apOverflow: N,
    override val apPattern: N,
    override val apLeave: N,
    override val tpClassified: N,
    override val tpOverflow: N,
    override val tpDepot: N,
    override val epPattern: N,
    override val epBase: N,
    override val rpBonus: N,
    override val rpMovement: N,
    override val rpGoal: N,
    override val rpPattern: N,
    val details: DecodeScoreDetailsAPI? = null,
) : MetricsAPI<N>, MetricsDecode<N> {
    constructor(team1: MetricsDecode<N>, team2: MetricsDecode<N>) : this(
        total = team1.total + team2.total,
        npTotal = team1.npTotal + team2.npTotal,
        auto = team1.auto + team2.auto,
        teleop = team1.teleop + team2.teleop,
        endgame = team1.endgame + team2.endgame,
        penalty = team1.penalty + team2.penalty,
        apClassified = team1.apClassified + team2.apClassified,
        apOverflow = team1.apOverflow + team2.apOverflow,
        apPattern = team1.apPattern + team2.apPattern,
        apLeave = team1.apLeave + team2.apLeave,
        tpClassified = team1.tpClassified + team2.tpClassified,
        tpOverflow = team1.tpOverflow + team2.tpOverflow,
        tpDepot = team1.tpDepot + team2.tpDepot,
        epPattern = team1.epPattern + team2.epPattern,
        epBase = team1.epBase + team2.epBase,
        rpBonus = team1.rpBonus + team2.rpBonus,
        rpMovement = team1.rpMovement + team2.rpMovement,
        rpGoal = team1.rpGoal + team2.rpGoal,
        rpPattern = team1.rpPattern + team2.rpPattern,
    )
    
    companion object {
        inline operator fun <reified N : Number> invoke(team1: MetricsDecode<*>, team2: MetricsDecode<*>) = MetricsDecodeAPI(
            total = team1.total.convert<N>() + team2.total.convert<N>(),
            npTotal = team1.npTotal.convert<N>() + team2.npTotal.convert<N>(),
            auto = team1.auto.convert<N>() + team2.auto.convert<N>(),
            teleop = team1.teleop.convert<N>() + team2.teleop.convert<N>(),
            endgame = team1.endgame.convert<N>() + team2.endgame.convert<N>(),
            penalty = team1.penalty.convert<N>() + team2.penalty.convert<N>(),
            apClassified = team1.apClassified.convert<N>() + team2.apClassified.convert<N>(),
            apOverflow = team1.apOverflow.convert<N>() + team2.apOverflow.convert<N>(),
            apPattern = team1.apPattern.convert<N>() + team2.apPattern.convert<N>(),
            apLeave = team1.apLeave.convert<N>() + team2.apLeave.convert<N>(),
            tpClassified = team1.tpClassified.convert<N>() + team2.tpClassified.convert<N>(),
            tpOverflow = team1.tpOverflow.convert<N>() + team2.tpOverflow.convert<N>(),
            tpDepot = team1.tpDepot.convert<N>() + team2.tpDepot.convert<N>(),
            epPattern = team1.epPattern.convert<N>() + team2.epPattern.convert<N>(),
            epBase = team1.epBase.convert<N>() + team2.epBase.convert<N>(),
            rpBonus = team1.rpBonus.convert<N>() + team2.rpBonus.convert<N>(),
            rpMovement = team1.rpMovement.convert<N>() + team2.rpMovement.convert<N>(),
            rpGoal = team1.rpGoal.convert<N>() + team2.rpGoal.convert<N>(),
            rpPattern = team1.rpPattern.convert<N>() + team2.rpPattern.convert<N>(),
        )
    }
}

inline fun <reified N : Number> MetricsDecode<*>.convert() = serialize { convert<N>() }

@Serializable
data class DecodeScoreDetailsAPI(
    override val randomization: Int,
    override val asPattern: List<DecodeScoreDetails.Classification>,
    override val esPattern: List<DecodeScoreDetails.Classification>,
    override val esRobot1: DecodeScoreDetails.BaseLocation,
    override val esRobot2: DecodeScoreDetails.BaseLocation,
) : DecodeScoreDetails

fun DecodeScoreDetails.serializeDetails() = DecodeScoreDetailsAPI(
    randomization,
    asPattern,
    esPattern,
    esRobot1,
    esRobot2,
)

fun <N : Number> MetricsDecode<N>.serialize() = MetricsDecodeAPI(
    total = total,
    npTotal = npTotal,
    auto = auto,
    teleop = teleop,
    endgame = endgame,
    penalty = penalty,
    apClassified = apClassified,
    apOverflow = apOverflow,
    apPattern = apPattern,
    apLeave = apLeave,
    tpClassified = tpClassified,
    tpOverflow = tpOverflow,
    tpDepot = tpDepot,
    epPattern = epPattern,
    epBase = epBase,
    rpBonus = rpBonus,
    rpMovement = rpMovement,
    rpGoal = rpGoal,
    rpPattern = rpPattern,
    details = if (this is DecodeScoreDetails) serializeDetails() else null,
)

@JvmName("serializeGeneric")
fun <T : Number, R : Number> MetricsDecode<T>.serialize(serializer: T.() -> R) = MetricsDecodeAPI(
    total = total.serializer(),
    npTotal = npTotal.serializer(),
    auto = auto.serializer(),
    teleop = teleop.serializer(),
    endgame = endgame.serializer(),
    penalty = penalty.serializer(),
    apClassified = apClassified.serializer(),
    apOverflow = apOverflow.serializer(),
    apPattern = apPattern.serializer(),
    apLeave = apLeave.serializer(),
    tpClassified = tpClassified.serializer(),
    tpOverflow = tpOverflow.serializer(),
    tpDepot = tpDepot.serializer(),
    epPattern = epPattern.serializer(),
    epBase = epBase.serializer(),
    rpBonus = rpBonus.serializer(),
    rpMovement = rpMovement.serializer(),
    rpGoal = rpGoal.serializer(),
    rpPattern = rpPattern.serializer(),
    details = if (this is DecodeScoreDetails) serializeDetails() else null,
)

@JvmName("serializeDouble")
fun MetricsDecode<Double>.serialize() = serialize { roundToInt().toShort() }

@JvmName("serializeNumber")
fun MetricsDecode<Number>.serialize() = serialize { toShort() }

fun MetricsDecode<*>.stringify() = """
        total: $total
        npTotal: $npTotal
        auto: $auto
        teleop: $teleop
        endgame: $endgame
        penalty: $penalty
        apClassified: $apClassified
        apOverflow: $apOverflow
        apPattern: $apPattern
        apLeave: $apLeave
        tpClassified: $tpClassified
        tpOverflow: $tpOverflow
        tpDepot: $tpDepot
        epPattern: $epPattern
        epBase: $epBase
        rpBonus: $rpBonus
        rpMovement: $rpMovement
        rpGoal: $rpGoal
        rpPattern: $rpPattern
    """.trimIndent()

object MetricsDecodeMeta : MetricsMeta<MetricsDecode<*>> {
    override val names = linkedMapOf(
        MetricsDecode<*>::total to "Total",
        MetricsDecode<*>::npTotal to "NP Total",
        MetricsDecode<*>::auto to "Auto",
        MetricsDecode<*>::teleop to "Teleop",
        MetricsDecode<*>::endgame to "Endgame",
        MetricsDecode<*>::penalty to "Foul",
        MetricsDecode<*>::apClassified to "Classified",
        MetricsDecode<*>::apOverflow to "Overflow",
        MetricsDecode<*>::apPattern to "Pattern",
        MetricsDecode<*>::apLeave to "Leave",
        MetricsDecode<*>::tpClassified to "Classified",
        MetricsDecode<*>::tpOverflow to "Overflow",
        MetricsDecode<*>::tpDepot to "Depot",
        MetricsDecode<*>::epPattern to "Pattern",
        MetricsDecode<*>::epBase to "Base",
        MetricsDecode<*>::rpBonus to "Bonus RP",
        MetricsDecode<*>::rpMovement to "Movement RP",
        MetricsDecode<*>::rpGoal to "Goal RP",
        MetricsDecode<*>::rpPattern to "Pattern RP",
    )
    
    override val periods = linkedMapOf(
        MetricsDecode<*>::total to ALL,
        MetricsDecode<*>::npTotal to ALL,
        MetricsDecode<*>::auto to AUTO,
        MetricsDecode<*>::teleop to TELEOP,
        MetricsDecode<*>::endgame to ENDGAME,
        MetricsDecode<*>::penalty to ALL,
        MetricsDecode<*>::apClassified to AUTO,
        MetricsDecode<*>::apOverflow to AUTO,
        MetricsDecode<*>::apPattern to AUTO,
        MetricsDecode<*>::apLeave to AUTO,
        MetricsDecode<*>::tpClassified to TELEOP,
        MetricsDecode<*>::tpOverflow to TELEOP,
        MetricsDecode<*>::tpDepot to TELEOP,
        MetricsDecode<*>::epPattern to ENDGAME,
        MetricsDecode<*>::epBase to ENDGAME,
        MetricsDecode<*>::rpBonus to ALL,
        MetricsDecode<*>::rpMovement to ALL,
        MetricsDecode<*>::rpGoal to ALL,
        MetricsDecode<*>::rpPattern to ALL,
    )
}
